/* eslint-disable react/prop-types */
import classNames from 'classnames';
import React, { useMemo } from 'react';
import { Form } from 'react-bootstrap';
import {
  useExpanded,
  useTable,
  useSortBy,
  usePagination,
  useRowSelect,
  useGlobalFilter
} from 'react-table';

export const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, className, ...rest }, ref) => {
    const defaultRef = React.useRef();

    const resolvedRef = ref || defaultRef;

    React.useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return (
      <div className={classNames('form-check fs-0 mb-0', className)}>
        <input
          type="checkbox"
          className="form-check-input"
          ref={resolvedRef}
          {...rest}
          style={{ cursor: 'pointer' }}
        />
      </div>
    );
  }
);

const ServerSideTableWrapper = ({
  children,
  columns,
  data,
  sortable,
  selection,
  selectionColumnWidth,
  canSelectRow,
  pagination,
  perPage,
  stickyColumns = []
}) => {
  const memoizedColumns = useMemo(() => {
    let stickyOffset = 0;
    return columns.map((column, index) => {
      const isSticky = stickyColumns.includes(index);
      const newColumn = {
        ...column,
        isSticky,
        stickyOffset: isSticky ? stickyOffset : undefined
      };
      if (isSticky) {
        // Estimate column width - adjust this calculation as needed
        const estimatedWidth = Math.max(
          (column.Header?.length || 0) * 10,
          data.reduce(
            (max, row) =>
              Math.max(max, String(row[column.accessor]).length * 8),
            0
          )
        );
        stickyOffset += estimatedWidth;
      }
      return newColumn;
    });
  }, [columns, data, stickyColumns]);

  const {
    getTableProps,
    headers,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    setPageSize,
    gotoPage,
    pageCount,
    state: { pageIndex, pageSize, selectedRowIds, globalFilter },
    setGlobalFilter
  } = useTable(
    {
      columns: memoizedColumns,
      data,
      disableSortBy: !sortable,
      autoResetSelectedRows: false,
      initialState: {
        pageSize: pagination ? perPage : data.length
      }
    },
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect,
    hooks => {
      if (selection) {
        hooks.visibleColumns.push(columns => [
          {
            id: 'selection',
            Header: ({ getToggleAllRowsSelectedProps }) => (
              <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
            ),
            headerProps: {
              style: {
                width: selectionColumnWidth
              }
            },
            cellProps: {
              style: {
                width: selectionColumnWidth
              }
            },
            Cell: ({ row }) => {
              if (canSelectRow && !canSelectRow(row)) {
                return null;
              }
              return (
                <div>
                  <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
                </div>
              );
            }
          },
          ...columns
        ]);
      }
    }
  );

  const recursiveMap = children => {
    return React.Children.map(children, child => {
      if (child.props?.children) {
        return React.cloneElement(child, {
          children: recursiveMap(child.props.children)
        });
      } else {
        if (child.props?.table) {
          return React.cloneElement(child, {
            ...child.props,
            getTableProps,
            headers,
            page,
            prepareRow,
            canPreviousPage,
            canNextPage,
            nextPage,
            previousPage,
            gotoPage,
            pageCount,
            pageIndex,
            selectedRowIds,
            pageSize,
            setPageSize,
            globalFilter,
            setGlobalFilter
          });
        } else {
          return child;
        }
      }
    });
  };

  return <>{recursiveMap(children)}</>;
};

export default ServerSideTableWrapper;
